/**
 * Columns — a single-series column chart with a horizontal baseline (spec §6.3: category
 * trend, and the returns/fees trend of BRIEF §9.3). Value labels only on the maximum and on
 * the current (partial) column; the baseline label sits at the LEFT end so it never collides.
 */

import { Figure, type TableTwin } from './Figure.tsx';
import { ChartTooltip, useTooltip, type TipContent } from './Tooltip.tsx';
import { axisLabel, bandScale, ticksFor, topRound } from './chart-utils.ts';
import { useChartWidth } from './useChartWidth.ts';

export interface ColumnPoint {
  label: string;
  /** agorot */
  value: number;
  aria: string;
  tip: TipContent;
}

export function Columns({
  question,
  period,
  summary,
  points,
  baseline,
  baselineLabel,
  currentIndex = -1,
  step,
  minTop = 0,
  height = 220,
  table,
}: {
  question: string;
  period?: string;
  summary?: string;
  points: ColumnPoint[];
  /** agorot; omit for no baseline line. */
  baseline?: number;
  baselineLabel?: string;
  currentIndex?: number;
  step?: number;
  minTop?: number;
  height?: number;
  table?: TableTwin;
}) {
  const [ref, width] = useChartWidth(720);
  const tooltip = useTooltip();
  if (!points.length) return <div ref={ref} />;

  const ml = 56;
  const mr = 16;
  const mt = 22;
  const mb = 30;
  const values = points.map((p) => p.value);
  const maxValue = Math.max(...values, baseline ?? 0);
  const ticks = ticksFor(Math.max(maxValue * 1.05, minTop), 5, step);
  const { band, center } = bandScale(points.length, ml, width - mr);
  const barWidth = Math.min(24, Math.floor(band * 0.5));
  const py = (value: number) => mt + ((ticks.top - value) / ticks.top) * (height - mt - mb);
  const maxIndex = values.indexOf(Math.max(...values));

  return (
    <div ref={ref}>
      <Figure question={question} period={period} summary={summary} table={table}>
        <svg viewBox={`0 0 ${width} ${height}`}>
          {ticks.ticks.map((tick) => (
            <g key={tick}>
              <line className="grid" x1={ml} x2={width - mr} y1={py(tick).toFixed(1)} y2={py(tick).toFixed(1)} />
              <text x={ml - 8} y={(py(tick) + 4).toFixed(1)} textAnchor="end">
                {axisLabel(tick)}
              </text>
            </g>
          ))}
          <line className="axis" x1={ml} x2={width - mr} y1={py(0).toFixed(1)} y2={py(0).toFixed(1)} />
          {points.map((point, index) => {
            const y = py(point.value);
            const h = py(0) - y;
            const handlers = tooltip.bind(point.tip);
            return (
              <g key={`${point.label}-${index}`}>
                <g className="seg" tabIndex={0} role="img" aria-label={point.aria} {...handlers}>
                  {h > 0 ? (
                    <path d={topRound(center(index) - barWidth / 2, y, barWidth, h, 4)} className="s1" opacity={currentIndex === index ? 0.6 : 1} />
                  ) : (
                    <rect x={(center(index) - barWidth / 2).toFixed(1)} y={(py(0) - 12).toFixed(1)} width={barWidth} height={12} className="hit" />
                  )}
                </g>
                {(index === maxIndex || index === currentIndex) && point.value > 0 ? (
                  <text x={center(index).toFixed(1)} y={(y - 6).toFixed(1)} textAnchor="middle" className="strong">
                    {axisLabel(Math.round(point.value))}
                  </text>
                ) : null}
                <text x={center(index).toFixed(1)} y={height - 8} textAnchor="middle" className={currentIndex === index ? 't1' : undefined}>
                  {point.label}
                  {currentIndex === index ? ' · עד כה' : ''}
                </text>
              </g>
            );
          })}
          {baseline ? (
            <g>
              <line className="base" x1={ml} x2={width - mr} y1={py(baseline).toFixed(1)} y2={py(baseline).toFixed(1)} />
              <text x={ml + 4} y={(py(baseline) - 5).toFixed(1)} textAnchor="start" className="strong bidi">
                {baselineLabel}
              </text>
            </g>
          ) : null}
        </svg>
      </Figure>
      <ChartTooltip state={tooltip.node} />
    </div>
  );
}
